declare global {
	interface Window {
		FWP: any;
		jQuery: any;
	}
}

import Cookies from 'js-cookie';

/**
 * Initialize FacetWP reload logic.
 *
 * This function overrides the history API to track URL changes and
 * triggers a page reload when FacetWP finishes an AJAX refresh if
 * the URL has changed from the initial state.
 */
const initFacetWPReload = () => {
	const $ = window.jQuery;
	if ( ! $ ) {
		return;
	}

	// --- History Override Logic ---
	const wrapHistoryMethod = ( method: 'pushState' | 'replaceState' ) => {
		const originalMethod = history[ method ];
		return function( this: History, ...args: any[] ) {
			const returnValue = originalMethod.apply( this, args );
			const customEvent = new Event( method + 'State' );
			window.dispatchEvent( customEvent );
			return returnValue;
		};
	};

	history.pushState = wrapHistoryMethod( 'pushState' );
	history.replaceState = wrapHistoryMethod( 'replaceState' );

	// --- URL Tracking Logic ---
	const initialUrl = window.location.href;
	let lastFullUrl = window.location.href;

	const updateLastUrl = () => {
		const currentUrl = window.location.href;
		// console.log( 'Previous URL:', lastFullUrl );
		// console.log( 'New URL:', currentUrl );
		lastFullUrl = currentUrl;
		// Store the URL in localStorage as per user example.
		localStorage.setItem( 'lastAppUrl', currentUrl );
	};

	// --- Event Listeners ---
	window.addEventListener( 'popstate', updateLastUrl );
	window.addEventListener( 'pushStateState', updateLastUrl as EventListener );
	window.addEventListener( 'replaceStateState', updateLastUrl as EventListener );

	/**
	 * Event listener for view switches (float/stacked).
	 */
	const handleViewClick = ( event: MouseEvent ) => {
		const viewElement = event.currentTarget as HTMLElement;

		const blockAncestor = viewElement.closest( '.wp-block-crsc-facetwp-product-collection' );
		if ( ! blockAncestor ) {
			return;
		}

		const blockId = blockAncestor.id || 'default';
		const viewType = viewElement.getAttribute( 'data-view' );

		if ( ! viewType ) {
			return;
		}

		const cookieName = `facetwp-product-collection-view-${blockId}`;

		Cookies.set( cookieName, viewType, { expires: 30 } );
		if ( Cookies.get( cookieName ) === viewType ) {
			reloadPageWithNewParams( {
				store_view: viewType
			} );
		}
	};

	/**
	 * Updates the current page URL with new query parameters and reloads the page.
	 *
	 * This function takes a collection of key-value pairs, updates the URL's query
	 * parameters with the provided values, and reloads the page to reflect the changes.
	 * If a parameter with an identical key already exists, it will be replaced by the
	 * new value.
	 *
	 * @param {Object} newParams - An object containing key-value pairs to update or add
	 *                             to the URL's query string.
	 */
	const reloadPageWithNewParams = (newParams: any) => {
		const url = new URL( window.location.href );

		const searchParams = url.searchParams;

		for ( const key in newParams ) {
			if ( Object.hasOwnProperty.call( newParams, key ) ) {
				searchParams.set( key, newParams[key] );
			}
		}

		window.location.href = url.href;
	}

	/**
	 * Attach event listeners to view switch elements.
	 */
	const attachViewListeners = () => {
		const viewElements = document.querySelectorAll( '.view-switch' );
		viewElements.forEach( ( el ) => {
			el.removeEventListener( 'click', handleViewClick as EventListener );
			el.addEventListener( 'click', handleViewClick as EventListener );
		} );
	};

	// Initial tracking call.
	updateLastUrl();
	attachViewListeners();

	/**
	 * Initialize FacetWP drawer logic.
	 */
	const initFacetDrawer = () => {
		const collections = document.querySelectorAll<HTMLElement>( '.crsc-facetwp-product-collection[data-enable-drawer="true"]' );

		collections.forEach( ( collection, index ) => {
			const breakpoint = collection.dataset.drawerBreakpoint || '768px';
			const buttonText = collection.dataset.drawerButtonText || 'Show Filters';
			const side = collection.dataset.drawerSide || 'right';
			const bgColor = collection.dataset.drawerBgColor || '#ffffff';
			const overlayColor = collection.dataset.drawerOverlayColor || 'rgba(0,0,0,0.5)';
			const widthPercentage = collection.dataset.drawerWidthPercentage || '70';

			// Create toggle button
			const toggleButton = document.createElement( 'button' );
			toggleButton.className = 'facet-drawer-toggle-button';
			toggleButton.textContent = buttonText;
			toggleButton.type = 'button';
			collection.prepend( toggleButton );

			// Create drawer
			const drawer = document.createElement( 'div' );
			drawer.className = `facet-drawer facet-drawer-side-${side}`;
			drawer.style.setProperty( '--drawer-bg-color', bgColor );
			drawer.style.setProperty( '--drawer-overlay-color', overlayColor );
			drawer.style.setProperty( '--drawer-width', `${widthPercentage}%` );

			const overlay = document.createElement( 'div' );
			overlay.className = 'facet-drawer-overlay';
			drawer.appendChild( overlay );

			const content = document.createElement( 'div' );
			content.className = 'facet-drawer-content';

			const closeButton = document.createElement( 'div' );
			closeButton.className = 'facet-drawer-close';
			closeButton.innerHTML = '&times;';
			content.appendChild( closeButton );

			drawer.appendChild( content );
			document.body.appendChild( drawer );

			const openDrawer = () => {
				drawer.classList.add( 'is-open' );
				document.body.classList.add( 'facet-drawer-open' );
			};

			const closeDrawer = () => {
				drawer.classList.remove( 'is-open' );
				document.body.classList.remove( 'facet-drawer-open' );
			};

			toggleButton.addEventListener( 'click', openDrawer );
			overlay.addEventListener( 'click', closeDrawer );
			closeButton.addEventListener( 'click', closeDrawer );

			const updateDrawerContent = () => {
				const facetSelection = collection.querySelector( '.wp-block-crsc-facet-selection' );
				if ( facetSelection ) {
					// Remove existing content except close button
					Array.from( content.childNodes ).forEach( child => {
						if ( child !== closeButton ) {
							content.removeChild( child );
						}
					} );

					const clonedSelection = facetSelection.cloneNode( true ) as HTMLElement;
					content.appendChild( clonedSelection );
				}
			};

			updateDrawerContent();
			document.addEventListener( 'facetwp-loaded', updateDrawerContent );

			// Dynamic style for breakpoint
			const styleId = `facet-drawer-style-${index}`;
			if ( ! document.getElementById( styleId ) ) {
				const style = document.createElement( 'style' );
				style.id = styleId;
				style.innerHTML = `
					@media (max-width: ${breakpoint}) {
						.crsc-facetwp-product-collection[data-enable-drawer="true"] .facet-drawer-toggle-button {
							display: block !important;
						}
						.crsc-facetwp-product-collection[data-enable-drawer="true"] .wp-block-crsc-facet-selection .wp-block-crsc-facet-item:not(.crsc-facetwp-facet-type-pager) {
							display: none;
						}
					}
				`;
				document.head.appendChild( style );
			}
		} );
	};

	initFacetDrawer();

	const reloadFacetPageOnFilter = () => {
		var collection_div = null;
		var collection_found = null;
		var collect_div_parent = null;

		document.addEventListener('facetwp-refresh', () => {
			if(null !== collection_found && collect_div_parent && collection_div ) {
				collect_div_parent.innerHTML = collection_div.outerHTML;
			}
		});

		// Watch for FacetWP loaded event.
		document.addEventListener('facetwp-loaded', () => {
			attachViewListeners();

			if (null === collection_found) {
				collection_found = document.querySelector( '.crsc-facetwp-facets-container' );
				collection_div = document.querySelector( '.crsc-facetwp-product-collection .facetwp-template' );
				if ( collection_div ) {
					collection_div = document.querySelector( '.crsc-facetwp-product-collection' );
					collect_div_parent = collection_div.parentNode;
				}
			}
			// If FacetWP has finished an AJAX refresh, or if the query parameters have changed.
			// We reload the page to ensure all blocks (like WooCommerce Product Collection)
			// are properly re-initialized with the new query.
			if ( window.location.href !== initialUrl ) {
				if (null !== collection_found && collect_div_parent && collection_div ) {
					collect_div_parent.innerHTML = collection_div.outerHTML;
				}

				var refreshed_div = document.querySelector( '.crsc-facetwp-product-collection' );
				if ( refreshed_div && collection_div ) {
					refreshed_div.innerHTML = collection_div.innerHTML;
				}
				window.location.reload();
			}
		} );
	}

	/**
	 * Re-hydrates the Interactivity API on new elements.
	 * This is necessary because FacetWP replaces the DOM via AJAX,
	 * but the Interactivity API needs to be told to scan and initialize new elements.
	 */
	const rehydrateInteractivity = async () => {
		try {
			// @ts-ignore - Dynamically import the interactivity API from the script module.
			const { privateApis } = await import( /* @vite-ignore */ '@wordpress/interactivity' );
			const { getRegionRootFragment, toVdom, render, initialVdomPromise } = privateApis( "I acknowledge that using private APIs means my theme or plugin will inevitably break in the next version of WordPress." );

			const initialVdom = await initialVdomPromise;
			const nodes = document.querySelectorAll( '.facetwp-template [data-wp-interactive]' );

			for ( const node of nodes ) {
				// Avoid re-hydrating already hydrated elements if they somehow persisted.
				if ( initialVdom && initialVdom.has( node ) ) {
					continue;
				}

				try {
					const fragment = getRegionRootFragment( node );
					const vdom = toVdom( node );

					if ( initialVdom ) {
						initialVdom.set( node, vdom );
					}

					// In WordPress 7.0 / Preact, passing a function as the 3rd argument to render triggers hydration.
					// This is equivalent to calling the internal `hydrate` (U) function.
					render( vdom, fragment, () => {} );
				} catch ( e ) {
					// Fallback for nodes that might fail re-hydration.
				}
			}
		} catch ( e ) {
			// console.error( 'Failed to re-hydrate interactivity:', e );
		}
	};

	const setupFacetWPInteractivity = () => {
		document.addEventListener( 'facetwp-loaded', () => {
			attachViewListeners();
			rehydrateInteractivity();
		} );
	};

	setupFacetWPInteractivity();
};

// Start the observer.
initFacetWPReload();
